import { type ReactNode } from "react";
import {
  ClockCircleOutlined,
  FilterOutlined,
  MailOutlined,
  MessageOutlined,
  MobileOutlined,
  SettingOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { Tag } from "antd";
import { type TFunction } from "i18next";

import {
  AutomationStartPoint,
  CheckoutVariantFilterType,
  DiscordIntegrationTask,
  EmailClientTask,
  FitnessAppTask,
  PriceFilterType,
  ProductTask,
  TaskType,
  type AutomationFilter,
  type AutomationTask,
  type EmailList,
  type EmailTaskGroup,
  type FieldValuesEmailClient,
  type FieldValuesEmailList,
  type TagsEmailClient,
  type TagsEmailList,
} from "@fitness-app/data-models/entities/Automation";

export type TaskCategory = "app" | "email" | "discord" | "product" | "other";

export const CATEGORY_ORDER: TaskCategory[] = ["app", "email", "discord", "product", "other"];

export const CATEGORY_META: Record<TaskCategory, { color: string; icon: ReactNode }> = {
  app: { color: "blue", icon: <MobileOutlined /> },
  email: { color: "gold", icon: <MailOutlined /> },
  discord: { color: "purple", icon: <MessageOutlined /> },
  product: { color: "green", icon: <ShoppingOutlined /> },
  other: { color: "default", icon: <SettingOutlined /> },
};

export interface SummaryItem {
  label: string;
  values: string[];
}

export interface ReminderAction {
  title: string;
  groups: SummaryItem[];
}

export interface TaskReminder {
  afterDays: number;
  actions: ReminderAction[];
}

export interface TaskDescription {
  category: TaskCategory;
  /** Human readable "what does this task do" config groups. */
  details: SummaryItem[];
  /** Price / checkout-variant filters (previously invisible from the list). */
  filters: SummaryItem[];
  /** "Send reminder about access" sub-tasks (only AddClientToApp). */
  reminders: TaskReminder[];
}

export interface DescribeContext {
  t: TFunction<string[]>;
  /** productId -> { name, price options } */
  productsById: Record<string, { name: string; prices: { label: string; value: string }[] | null }>;
  /** priceId -> readable price label */
  pricesById: Record<string, string>;
  /** checkoutVariantId -> slug */
  checkoutVariantsById: Record<string, string>;
  /** discord guildId -> server name */
  discordServersById: Record<string, string>;
}

export const getTaskCategory = (task: AutomationTask): TaskCategory => {
  switch (task.type) {
    case TaskType.FitnessApp:
      return "app";
    case TaskType.EmailClient:
      return "email";
    case TaskType.DiscordIntegration:
      return "discord";
    case TaskType.ProductTask:
      return "product";
    default:
      return "other";
  }
};

export const getTaskTitle = (task: AutomationTask, t: TFunction<string[]>): string =>
  t(`common:${task.type}.${task.action.type}`);

const s = (t: TFunction<string[]>, key: string, options?: Record<string, unknown>): string =>
  t(`products:automationTasks.summary.${key}`, options ?? {});

const group = (label: string, values: (string | undefined | null)[] | null | undefined): SummaryItem => ({
  label,
  values: (values ?? []).filter((value): value is string =>
    Boolean(value && typeof value === "string" && value.trim()),
  ),
});

type EmailTarget = TagsEmailClient | TagsEmailList | EmailList | FieldValuesEmailClient | FieldValuesEmailList;

const formatEmailTarget = (item: EmailTarget, t: TFunction<string[]>): string => {
  const mailer = t(`common:mailer.${item.client}`);
  return "name" in item && item.name ? `${item.name} (${mailer})` : mailer;
};

const formatStartPoint = (start: AutomationStartPoint | undefined, t: TFunction<string[]>): string => {
  if (start === AutomationStartPoint.StartOfWeek) return s(t, "startStartOfWeek");
  if (start === AutomationStartPoint.AfterInitialSurvey) return s(t, "startAfterInitialSurvey");
  return s(t, "startAfterPurchase");
};

const formatProgram = (
  program: { name: string; startAutomationFrom?: AutomationStartPoint } | null | undefined,
  t: TFunction<string[]>,
): string | undefined => {
  if (!program) {
    return undefined;
  }
  const name = program.name || s(t, "programUnnamed");
  return program.startAutomationFrom ? `${name} · ${formatStartPoint(program.startAutomationFrom, t)}` : name;
};

const formatProduct = (
  product: { productId: string; priceId?: string | null; accessSource?: string; followSource?: boolean },
  ctx: DescribeContext,
): string => {
  const meta = ctx.productsById[product.productId];
  const name = meta?.name ?? product.productId;
  if (product.accessSource === "trigger_product") {
    return `${name} — ${ctx.t(
      product.followSource === false
        ? "products:automationTaskSummary.accessFromTriggerFrozen"
        : "products:automationTaskSummary.accessFromTrigger",
    )}`;
  }
  if (!product.priceId) {
    return name;
  }
  const priceLabel = meta?.prices?.find((price) => price.value === product.priceId)?.label;
  return priceLabel ? `${name} — ${priceLabel}` : name;
};

const describeEmailAction = (action: EmailTaskGroup, t: TFunction<string[]>): SummaryItem[] => {
  switch (action.type) {
    case EmailClientTask.AddTags:
    case EmailClientTask.RemoveTags:
      return [
        group(s(t, "tags"), action.tags),
        group(
          s(t, "targets"),
          action.emailClients?.map((item) => formatEmailTarget(item, t)),
        ),
      ];
    case EmailClientTask.AddToLists:
    case EmailClientTask.RemoveFromLists:
      return [
        group(
          s(t, "lists"),
          action.emailLists?.map((item) => formatEmailTarget(item, t)),
        ),
      ];
    case EmailClientTask.AddFieldValues:
      return [
        group(
          s(t, "fields"),
          Object.entries(action.values ?? {}).map(([key, value]) => `${key}: ${String(value)}`),
        ),
        group(
          s(t, "targets"),
          action.emailLists?.map((item) => formatEmailTarget(item, t)),
        ),
      ];
    default:
      return [];
  }
};

const describeFilters = (filters: AutomationFilter[] | null | undefined, ctx: DescribeContext): SummaryItem[] => {
  if (!filters?.length) {
    return [];
  }
  const { t } = ctx;
  const out: SummaryItem[] = [];

  for (const filter of filters) {
    if (!filter) {
      continue;
    }
    if ("prices" in filter) {
      const label = filter.type === PriceFilterType.Excluded ? s(t, "excludedPrices") : s(t, "includedPrices");
      out.push(
        group(
          label,
          filter.prices?.map((id) => ctx.pricesById[id] ?? id),
        ),
      );
    } else {
      const label =
        filter.type === CheckoutVariantFilterType.Excluded
          ? s(t, "excludedCheckoutVariants")
          : s(t, "includedCheckoutVariants");
      out.push(
        group(
          label,
          filter.checkoutVariants?.map((id) => ctx.checkoutVariantsById[id] ?? id),
        ),
      );
    }
  }

  return out.filter((item) => item.values.length);
};

export const describeAutomationTask = (task: AutomationTask, ctx: DescribeContext): TaskDescription => {
  if (!task?.action) {
    return { category: "other", details: [], filters: [], reminders: [] };
  }
  const { t } = ctx;
  const details: SummaryItem[] = [];
  const reminders: TaskReminder[] = [];

  if (task.type === TaskType.EmailClient) {
    details.push(...describeEmailAction(task.action, t));
  }

  if (task.type === TaskType.DiscordIntegration) {
    const action = task.action;
    details.push(group(s(t, "server"), [ctx.discordServersById[action.serverId] ?? action.serverId]));
    if (action.type === DiscordIntegrationTask.AddRole || action.type === DiscordIntegrationTask.RemoveRole) {
      details.push(group(s(t, "role"), [action.roleName]));
    }
  }

  if (task.type === TaskType.FitnessApp) {
    const action = task.action;
    switch (action.type) {
      case FitnessAppTask.AddClientToApp: {
        details.push(group(s(t, "access"), [t(`common:accessType.${action.accessType}`)]));
        const program = formatProgram(action.programAutomation, t);
        if (program) {
          details.push(group(s(t, "program"), [program]));
        }
        details.push(group(s(t, "tags"), action.tags));
        if (Array.isArray(action.sendReminderAboutAccess) && action.sendReminderAboutAccess.length) {
          details.push(
            group(s(t, "reminders"), [s(t, "remindersCount", { value: action.sendReminderAboutAccess.length })]),
          );
          reminders.push(
            ...action.sendReminderAboutAccess.map((reminder) => ({
              afterDays: reminder.afterDays,
              actions: reminder.actions.map((reminderAction) => ({
                title: t(`common:emailClient.${reminderAction.type}`),
                groups: describeEmailAction(reminderAction, t),
              })),
            })),
          );
        }
        break;
      }
      case FitnessAppTask.AddProgramAutomationToClient:
        details.push(group(s(t, "program"), [formatProgram(action.programAutomation, t)]));
        break;
      case FitnessAppTask.AddTagsToTraineeProfile:
      case FitnessAppTask.RemoveTagsFromTraineeProfile:
        details.push(group(s(t, "tags"), action.tags));
        break;
      case FitnessAppTask.ActivatedCustomerFeature:
      case FitnessAppTask.DeactivatedCustomerFeature:
        details.push(
          group(
            s(t, "features"),
            action.features.map((feature) => t(`common:customerFeatures.${feature}`)),
          ),
        );
        break;
      default:
        break;
    }
  }

  if (task.type === TaskType.ProductTask) {
    const action = task.action;
    details.push(
      group(
        s(t, "products"),
        action.products.map((product) => formatProduct(product, ctx)),
      ),
    );
    const extras: string[] = [];
    if (action.type === ProductTask.ImportIntoProduct && action.sendEmailAboutAddToProduct) {
      extras.push(s(t, "sendEmailAboutAddToProduct"));
    }
    if (action.type === ProductTask.RemoveFromProduct && action.cancelActiveSubscription) {
      extras.push(s(t, "cancelActiveSubscription"));
    }
    if (extras.length) {
      details.push(group(s(t, "extra"), extras));
    }
  }

  return {
    category: getTaskCategory(task),
    details: details.filter((item) => item.values.length),
    filters: describeFilters(task.filters, ctx),
    reminders,
  };
};

/** Flattened, lowercased text used by the toolbar search. */
export const getTaskSearchText = (description: TaskDescription, title: string, extra: string[]): string =>
  [
    title,
    ...extra,
    ...description.details.flatMap((item) => [item.label, ...item.values]),
    ...description.filters.flatMap((item) => [item.label, ...item.values]),
    ...description.reminders.flatMap((reminder) =>
      reminder.actions.flatMap((action) => [action.title, ...action.groups.flatMap((g) => g.values)]),
    ),
  ]
    .join(" ")
    .toLowerCase();

interface SummaryRowProps {
  group: SummaryItem;
  max: number;
  color?: string;
  icon?: ReactNode;
}

const SummaryRow = ({ group: item, max, color, icon }: SummaryRowProps) => {
  const shown = item.values.slice(0, max);
  const rest = item.values.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1 leading-5">
      {icon}
      <span className="text-xs text-gray-400">{item.label}:</span>
      {shown.map((value, index) => (
        <Tag key={`${value}-${index}`} color={color} className="!m-0 max-w-[260px] truncate !text-xs">
          {value}
        </Tag>
      ))}
      {rest > 0 && <span className="text-xs text-gray-400">+{rest}</span>}
    </div>
  );
};

interface SummaryProps {
  description: TaskDescription;
  t: TFunction<string[]>;
  max?: number;
}

/** Compact, single-glance summary rendered inside each table row. */
export const AutomationTaskSummary = ({ description, t, max = 3 }: SummaryProps) => {
  const { details, filters } = description;

  if (!details.length && !filters.length) {
    return <span className="text-xs text-gray-400">{s(t, "noConfig")}</span>;
  }

  return (
    <div className="mt-0.5 flex flex-col gap-0.5">
      {details.map((item) => (
        <SummaryRow key={item.label} group={item} max={max} />
      ))}
      {filters.map((item, index) => (
        <SummaryRow
          key={item.label}
          group={item}
          max={max}
          color="orange"
          icon={index === 0 ? <FilterOutlined className="text-xs text-amber-500" /> : undefined}
        />
      ))}
    </div>
  );
};

const DetailSection = ({
  title,
  groups,
  color,
  emptyText,
}: {
  title: string;
  groups: SummaryItem[];
  color?: string;
  emptyText?: string;
}) => (
  <div>
    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</div>
    {groups.length ? (
      <div className="flex flex-col gap-1">
        {groups.map((item) => (
          <SummaryRow key={item.label} group={item} max={99} color={color} />
        ))}
      </div>
    ) : (
      <span className="text-xs text-gray-400">{emptyText}</span>
    )}
  </div>
);

/** Full, untruncated detail rendered inside the expandable row. */
export const AutomationTaskDetails = ({ description, t }: SummaryProps) => {
  const { details, filters, reminders } = description;

  return (
    <div className="flex flex-col gap-4 px-2 py-1">
      <DetailSection title={s(t, "config")} groups={details} emptyText={s(t, "noConfig")} />

      {filters.length > 0 && <DetailSection title={s(t, "filters")} groups={filters} color="orange" />}

      {reminders.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{s(t, "reminders")}</div>
          <div className="flex flex-col gap-2">
            {reminders.map((reminder, reminderIndex) => (
              <div key={reminderIndex} className="rounded-md bg-gray-50 p-2">
                <div className="mb-1 text-sm font-medium text-gray-700">
                  <ClockCircleOutlined className="mr-1" />
                  {s(t, "afterDays", { value: reminder.afterDays })}
                </div>
                {reminder.actions.map((action, actionIndex) => (
                  <div key={actionIndex} className="mb-1 ml-4">
                    <div className="text-xs font-medium text-gray-600">{action.title}</div>
                    {action.groups.map((item) => (
                      <SummaryRow key={item.label} group={item} max={99} />
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
